import ExcelJS from 'exceljs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { AnyAction } from 'redux';
import { isInRange, isBigger } from '../../../helper/ResultHelper';
import { JobType, MeterRateTaskType, MeterTaskResultType } from '../../../redux/jobs/jobs.types';
import { types as jobsActionTypes } from '../../../redux/jobs/jobs.actions';
import { ApplicationConfig, DefaultJobComment } from '../../../redux/application/application.types';
import { mergeJobConfig } from '../../../helper/JobConfigHelper';
import { getAllowedReadingDateRange } from '../../../helper/ReadingDateRangeHelper';
import { toReadingImagePayload } from '../../../helper/ReadingImagePayloadHelper';
import { shortenObis } from '../../../helper/MeterHelper';
import { ImportCategory, ImportGroup, ImportRow, ImportStats, RawExcelRow } from './ImportExcel.types';
import { download } from '../../../services/excel/ExcelExportDownloader';
import { dayjsTZ } from '../../../helper/DateHelper';
import i18n from '../../../i18n';

dayjs.extend(customParseFormat);

// ─── Pure helpers ─────────────────────────────────────────────────────────────

export function normalizeDecimal(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return isNaN(value) ? null : value;
  let s = String(value).trim();
  if (!s) return null;
  // Spaces are only ever a thousands grouping, never ambiguous — strip them
  // before validation so "1 234.5" normalizes to 1234.5 instead of being
  // rejected.
  s = s.replace(/\s+/g, '');
  // If comma present: dots are thousand separators, comma is decimal separator
  if (s.includes(',')) {
    s = s.replace(/\./g, '').replace(',', '.');
  }
  // parseFloat parses a leading numeric prefix and silently ignores the rest
  // (e.g. "123abc" -> 123) — validate the full string shape first so garbled
  // cell content is rejected instead of read as a plausible wrong number.
  if (!/^-?\d+(\.\d+)?$/.test(s)) return null;
  const num = parseFloat(s);
  return isNaN(num) ? null : num;
}

export function normalizeDateToISO(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  if (value instanceof Date) return dayjs(value).format('YYYY-MM-DD');
  const s = String(value).trim();
  if (!s) return null;
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(s)) {
    return dayjs(s, 'DD.MM.YYYY').format('YYYY-MM-DD');
  }
  const d = dayjs(s);
  return d.isValid() ? d.format('YYYY-MM-DD') : null;
}

export function toStartOfDayISO(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  return dayjsTZ(dateStr).startOf('day').toISOString();
}

export function toReadingImages(images: string[] = []) {
  return images.map((image) => toReadingImagePayload(image));
}

function fillMissingReadingDate(raw: RawExcelRow, importDate: string): RawExcelRow {
  if (raw.ablesewert !== null && raw.ablesedatum === null) {
    return { ...raw, ablesedatum: importDate };
  }

  return raw;
}

const normalizeMatchText = (value: string | null | undefined): string => String(value ?? '').trim().toLowerCase();

function resolveRawRowMatch(raw: RawExcelRow, jobsMap: Map<string, JobType>): Pick<RawExcelRow, 'jobId' | 'taskId'> {
  if (raw.jobId || raw.taskId) {
    return { jobId: raw.jobId, taskId: raw.taskId };
  }

  const meterNumber = normalizeMatchText(raw.zaehlernummer);
  if (!meterNumber) {
    return { jobId: null, taskId: null };
  }

  const obis = normalizeMatchText(raw.obis);
  const candidates: Array<{ jobId: string; taskId: string }> = [];

  jobsMap.forEach((job, jobId) => {
    if (normalizeMatchText(job.meter?.meterNumber) !== meterNumber) return;

    (job.meter?.meterRateTasks ?? []).forEach((task) => {
      // Der Export schreibt die gekürzte OBIS (shortenObis); ältere Dateien
      // und Fremdlisten enthalten die volle Form — beide werden akzeptiert.
      const fullObis = normalizeMatchText(task.obis);
      const shortObis = normalizeMatchText(shortenObis(task.obis));
      if (obis && fullObis !== obis && shortObis !== obis) return;
      candidates.push({ jobId, taskId: task.taskId });
    });
  });

  if (candidates.length !== 1) {
    return { jobId: null, taskId: null };
  }

  return candidates[0];
}

const IMPORTABLE_ROW_CATEGORIES = new Set<ImportCategory>(['plausible', 'implausible']);

export function isImportableRow(row: ImportRow): boolean {
  return IMPORTABLE_ROW_CATEGORIES.has(row.category);
}

export function toMeterTaskResults(
  rows: ImportRow[],
  comment: string | undefined,
  images: Record<string, string[]>
): MeterTaskResultType[] {
  return rows
    .filter((r) => r.taskId && r.ablesewert !== null && isImportableRow(r))
    .map((r) => ({
      taskId: r.taskId!,
      readingValue: r.ablesewert!,
      comment: comment || undefined,
      readingImages: toReadingImages(images[r.taskId!] ?? []),
    }));
}

// ─── Categorization ──────────────────────────────────────────────────────────

// True when the value has no more decimals than allowed (float-tolerant).
function decimalsWithinLimit(value: number, postDecimals: number): boolean {
  const factor = Math.pow(10, postDecimals);
  return Math.abs(value * factor - Math.round(value * factor)) < 1e-9;
}

// True when the integer part fits into the meter's register width (#60).
function preDecimalsWithinLimit(value: number, preDecimals: number): boolean {
  return Math.floor(Math.abs(value)) < Math.pow(10, preDecimals);
}

function latestByDate<T extends { readingDate?: string }>(arr?: T[]): T | undefined {
  if (!arr || arr.length === 0) return undefined;
  return arr.reduce((a, b) => (dayjs(a.readingDate).valueOf() > dayjs(b.readingDate).valueOf() ? a : b));
}

// Checks the local offline send-queue (not just the backend-confirmed
// task.readingResults) for a reading that was already enqueued for the same
// job/task/date with the SAME value. A queued entry for a DIFFERENT value is
// deliberately NOT treated as a duplicate here — that case must still surface
// normally (plausible/implausible), only exact re-submissions are silently
// ignored (see envago/ksa-pro-ui#325).
//
// The queue stores `readingDate` as a UTC ISO string produced via
// toStartOfDayISO (local-midnight-in-APP_TIMEZONE, converted to UTC) — it must
// be re-normalized with dayjsTZ before comparing against the Excel row's plain
// YYYY-MM-DD date, never with a naive slice(0, 10), or dates near midnight can
// resolve to the wrong day.
export function isReadingAlreadyQueued(raw: RawExcelRow, queuedActions: AnyAction[] = []): boolean {
  if (!raw.jobId || !raw.taskId || raw.ablesewert === null || !raw.ablesedatum) return false;

  return queuedActions.some((action) => {
    if (action?.type !== jobsActionTypes.SEND_METER_TASK_RESULTS || action.jobId !== raw.jobId) return false;
    if (!action.readingDate) return false;

    const queuedDateOnly = dayjsTZ(action.readingDate).format('YYYY-MM-DD');
    if (queuedDateOnly !== raw.ablesedatum) return false;

    const taskResults: Array<{ taskId?: string; readingValue?: number }> = action.taskResults ?? [];
    return taskResults.some(
      (tr) => tr.taskId === raw.taskId && Math.abs((tr.readingValue ?? 0) - raw.ablesewert!) < 0.001
    );
  });
}

export function categorizeRow(
  raw: RawExcelRow,
  jobsMap: Map<string, JobType>,
  applicationConfig?: ApplicationConfig,
  queuedActions?: AnyAction[]
): ImportCategory {
  if (!raw.jobId || !raw.taskId) return 'not_found';

  const job = jobsMap.get(raw.jobId);
  if (!job) return 'not_found';

  const task = job.meter?.meterRateTasks?.find((t) => t.taskId === raw.taskId);
  if (!task) return 'not_found';

  if (raw.ablesewert === null || raw.ablesedatum === null) return 'no_value';

  const dateOnly = raw.ablesedatum;
  const alreadyDone =
    task.readingResults?.some(
      (r) =>
        dayjs(r.readingDate).format('YYYY-MM-DD') === dateOnly &&
        Math.abs((r.readingValue ?? 0) - raw.ablesewert!) < 0.001
    ) || isReadingAlreadyQueued(raw, queuedActions);
  if (alreadyDone) return 'already_done';

  const merged = mergeJobConfig(job.config, applicationConfig);
  const strict = task.readingValueValidation?.strictValidation === true;
  const postDecimals = task.postDecimals ?? 0;

  // Nicht heilbar (wie JobDetails harter Block): falsche Nachkommastellen (nur wenn abgefragt).
  if (merged?.meterReading?.queryPostDecimals === true && !decimalsWithinLimit(raw.ablesewert, postDecimals)) {
    return 'invalid_reading';
  }

  // Nicht heilbar: der Wert überläuft das Zählwerk (zu viele Vorkommastellen, #60).
  if (typeof task.preDecimals === 'number' && task.preDecimals > 0 && !preDecimalsWithinLimit(raw.ablesewert, task.preDecimals)) {
    return 'invalid_reading';
  }

  const v = task.readingValueValidation;
  if (v?.minimumValue != null || v?.maximumValue != null) {
    // A missing bound is open-ended (analog JobDetails.tsx:161-162) — configuring
    // only one side must still validate the other, not skip the check entirely.
    const minimumValue = v.minimumValue ?? 0;
    const maximumValue = v.maximumValue ?? Number.MAX_SAFE_INTEGER;
    if (!isInRange(raw.ablesewert, minimumValue, maximumValue, postDecimals)) {
      // strict = nicht heilbar; sonst heilbar per Foto/Ablesehinweis
      return strict ? 'invalid_reading' : 'implausible';
    }
  }

  // Historic/Continuous sind nie hart (auch bei strict) — immer heilbar.
  if (merged?.features?.historicReadingsValidationRequired === true) {
    const lastEvent = latestByDate(task.readingEvents);
    if (lastEvent && !isBigger(raw.ablesewert, lastEvent.readingValue, postDecimals)) return 'implausible';
  }
  if (merged?.features?.continuousReadingsValidationRequired === true) {
    const lastResult = latestByDate(task.readingResults);
    if (lastResult && !isBigger(raw.ablesewert, lastResult.readingValue, postDecimals)) return 'implausible';
  }

  return 'plausible';
}

// `rows` must already be narrowed to a single "reading occasion" (rows whose
// tasks share the same targetReadingDate) — see groupRowsByJob. A job can
// legitimately contain tariffs scheduled for different future periods (e.g.
// one tariff read monthly for the next 3 months); those naturally carry
// different dates and must never be compared against each other here.
export function detectDateMismatch(rows: ImportRow[]): boolean {
  if (rows.length < 2) return false;
  const hasStrict = rows.some((r) => r.task?.readingValueValidation?.strictValidation === true);
  if (!hasStrict) return false;
  const dates = new Set(rows.map((r) => r.ablesedatum).filter(Boolean));
  return dates.size > 1;
}

const CATEGORY_PRIORITY: ImportCategory[] = [
  'not_found', 'expired', 'not_yet', 'duplicate_conflict',
  'invalid_reading_date', 'invalid_reading', 'date_mismatch', 'partial', 'implausible', 'no_value', 'already_done', 'plausible',
];

const DATE_MISMATCH_BLOCKERS = new Set<ImportCategory>([
  'not_found',
  'expired',
  'not_yet',
  'duplicate_conflict',
  'invalid_reading_date',
  'invalid_reading',
  'no_value',
]);

function worstCategory(rows: ImportRow[]): ImportCategory {
  for (const cat of CATEGORY_PRIORITY) {
    if (rows.some((r) => r.category === cat)) return cat;
  }
  return 'plausible';
}

export function groupRowsByJob(rows: ImportRow[]): ImportGroup[] {
  const byJob = new Map<string, ImportRow[]>();

  rows.forEach((row, idx) => {
    const key = row.jobId ?? `__nojob_${idx}`;
    if (!byJob.has(key)) byJob.set(key, []);
    byJob.get(key)!.push(row);
  });

  return Array.from(byJob.entries()).map(([jobId, jobRows]) => {
    let category = worstCategory(jobRows);
    let effectiveRows = jobRows;

    if (!DATE_MISMATCH_BLOCKERS.has(category)) {
      // Only rows whose tasks share the same targetReadingDate were meant to
      // be read together — cluster by that before checking for a conflict, so
      // a tariff scheduled over several distinct future periods (each with
      // its own, different, correct date) is never flagged against itself.
      const byTargetDate = new Map<string, ImportRow[]>();
      jobRows.forEach((r) => {
        const key = r.task?.targetReadingDate ?? '__no-target__';
        if (!byTargetDate.has(key)) byTargetDate.set(key, []);
        byTargetDate.get(key)!.push(r);
      });

      const conflicting = new Set<ImportRow>();
      byTargetDate.forEach((clusterRows) => {
        if (detectDateMismatch(clusterRows)) {
          clusterRows.forEach((r) => conflicting.add(r));
        }
      });

      if (conflicting.size > 0) {
        effectiveRows = jobRows.map((r) => (conflicting.has(r) ? { ...r, category: 'date_mismatch' as ImportCategory } : r));

        if (conflicting.size < jobRows.length) {
          // Partial conflict: the unaffected rows still go through the normal
          // plausible/implausible flow and get submitted with their own real
          // dates (see enqueueGroup) — only the conflicting cluster is
          // excluded and counted separately, never merged into one guessed date.
          category = worstCategory(effectiveRows.filter((r) => r.category !== 'date_mismatch'));
          if (category === 'plausible' || category === 'implausible') {
            category = 'partial';
          }
        } else {
          category = 'date_mismatch';
        }
      }
    }

    if (category === 'already_done') {
      // A tariff that was already submitted in an earlier import must never
      // suppress a genuinely new, resolvable reading for a SIBLING tariff of
      // the same job — recompute from the rest so it still reaches the normal
      // plausible/implausible flow. The already-done row itself is excluded
      // from submission separately (isImportableRow) and from re-export
      // (markRowsResolved), it just isn't lost or silently skipped.
      const notDone = effectiveRows.filter((r) => r.category !== 'already_done');
      if (notDone.length > 0) {
        category = worstCategory(notDone);
      }
    }

    if (category === 'plausible' || category === 'implausible') {
      const job = jobRows[0]?.job;
      const totalTasks = job?.meter?.meterRateTasks?.length ?? 0;
      if (totalTasks > jobRows.length) {
        category = 'partial';
      }
    }

    return {
      jobId,
      rows: effectiveRows,
      category,
      job: jobRows[0]?.job ?? null,
    };
  });
}

export function isReadingHintValid(comment: string | undefined): boolean {
  return (comment ?? '').replace(/\s/g, '').length >= 5;
}

// Trifft ein Ablesehinweis aus der Excel-Datei einen konfigurierten
// Standardhinweis (`config.meterReading.defaultComments`) wortgleich — Groß-/
// Kleinschreibung und umgebender Leerraum egal —, wird er auf dessen exakte
// Schreibweise normalisiert. „leerstand" wird damit zu „Leerstand" und im
// Review-Schritt als Standardhinweis ausgewählt, statt als freier Text zu
// landen. Ohne Treffer bleibt der Hinweis unverändert.
export function canonicalizeReadingHint(
  hint: string | null,
  defaultComments?: DefaultJobComment[]
): string | null {
  const trimmed = (hint ?? '').trim();
  if (!trimmed || !defaultComments?.length) return hint;

  const normalized = trimmed.toLowerCase();
  const match = defaultComments.find((comment) => (comment.text ?? '').trim().toLowerCase() === normalized);

  return match ? match.text : hint;
}

// Combines the per-row Ablesehinweis values from an Excel import into the single
// free-text comment the app sends per job. A single hint is used as-is; with
// multiple tariffs (rows), each hint is prefixed with its tariff so the reader
// can tell which reading it belongs to: "OBIS (Tarifname): Hinweis, ...".
export function combineReadingHints(rows: ImportRow[]): string {
  const withHints = rows.filter((r) => (r.ablesehinweis ?? '').trim() !== '');
  if (withHints.length === 0) return '';
  if (withHints.length === 1) return withHints[0].ablesehinweis!.trim();

  return withHints
    .map((r) => {
      // Same "Tarifname (kurzes OBIS)" format as MeterReadingTarifPlain.
      const shortObis = shortenObis(r.task?.obis);
      const tariff = r.task?.rateName
        ? shortObis ? `${r.task.rateName} (${shortObis})` : r.task.rateName
        : shortObis || r.taskId || '';
      return `${tariff}: ${r.ablesehinweis!.trim()}`;
    })
    .join(', ');
}

export function hasAnyPhoto(images: Record<string, string[]>): boolean {
  return Object.values(images).some((imgs) => imgs.length > 0);
}

export function canAdvance(
  requirePhoto: boolean,
  requireComment: boolean,
  imagesForbidden: boolean,
  commentsEnabled: boolean,
  images: Record<string, string[]>,
  comment: string,
  photoRequiredTaskIds?: string[]
): boolean {
  const effectiveRequirePhoto = requirePhoto && !imagesForbidden;
  const effectiveRequireComment = requireComment && commentsEnabled;
  const hasPhoto = photoRequiredTaskIds && photoRequiredTaskIds.length > 0
    ? photoRequiredTaskIds.every((taskId) => (images[taskId] ?? []).length > 0)
    : hasAnyPhoto(images);
  const hasComment = isReadingHintValid(comment);
  const imageOrCommentRequired = effectiveRequirePhoto && effectiveRequireComment;

  return (
    (!effectiveRequirePhoto || imageOrCommentRequired || hasPhoto) &&
    (!effectiveRequireComment || imageOrCommentRequired || hasComment) &&
    (!imageOrCommentRequired || hasPhoto || hasComment)
  );
}

export function countRowsByCategory(groups: ImportGroup[], category: ImportCategory): number {
  return groups.reduce((sum, group) => sum + group.rows.filter((row) => row.category === category).length, 0);
}

// Plausible rows belonging to a group whose overall category isn't 'plausible'
// (e.g. a plausible row inside a 'partial' group) are handled elsewhere in the
// flow, not on this step — so only fully-plausible groups count here.
export function countPlausibleGroupRows(groups: ImportGroup[]): number {
  return groups.reduce(
    (sum, group) => sum + (group.category === 'plausible' ? group.rows.filter((row) => row.category === 'plausible').length : 0),
    0
  );
}

// Maps a non-importable row/group category to its ImportStats reason bucket.
const CATEGORY_TO_BUCKET: Partial<Record<ImportCategory, keyof ImportStats>> = {
  not_found: 'notFound',
  no_value: 'noValue',
  duplicate_conflict: 'duplicateConflict',
  duplicate_exact: 'duplicateExact',
  invalid_reading_date: 'invalidReadingDate',
  invalid_reading: 'invalidReading',
  date_mismatch: 'dateMismatch',
  expired: 'expired',
  not_yet: 'notYet',
  already_done: 'alreadyDone',
};

// Group categories that resolve PER ROW (the group is not a single blocking verdict).
const PER_ROW_GROUP_CATEGORIES = new Set<ImportCategory>(['plausible', 'implausible', 'partial']);

const emptyStats = (total: number): ImportStats => ({
  total,
  submitted: 0,
  submittedPlausible: 0,
  submittedImplausible: 0,
  implausibleSkipped: 0,
  skipped: 0,
  notFound: 0,
  noValue: 0,
  duplicateConflict: 0,
  duplicateExact: 0,
  invalidReadingDate: 0,
  invalidReading: 0,
  dateMismatch: 0,
  expired: 0,
  notYet: 0,
  alreadyDone: 0,
});

/**
 * Single-pass reconciliation of import outcomes. Each Excel row lands in EXACTLY
 * one bucket, so `submittedPlausible + submittedImplausible + skipped +
 * implausibleSkipped + Σ(reason buckets) === total`.
 *
 * Hybrid precedence:
 *  - Blocker groups (category not plausible/implausible/partial) → ALL rows count
 *    under the group's reason bucket, even if an individual row looks plausible.
 *  - plausible/implausible/partial groups → resolve per row:
 *      importable + decision 'submitted'          → submittedPlausible/Implausible
 *      importable + 'skipped' or undecided         → skipped / implausibleSkipped
 *      not importable                              → its own reason bucket
 */
export function computeImportStats(groups: ImportGroup[], totalRows: number): ImportStats {
  const stats = emptyStats(totalRows);

  for (const group of groups) {
    if (!PER_ROW_GROUP_CATEGORIES.has(group.category)) {
      const bucket = CATEGORY_TO_BUCKET[group.category];
      if (bucket) stats[bucket] += group.rows.length;
      continue;
    }

    for (const row of group.rows) {
      if (isImportableRow(row)) {
        const isPlausible = row.category === 'plausible';
        // A row's own decision (set when a group is split, e.g. "import only
        // the plausible readings") takes precedence over the group's.
        const decision = row.decision ?? group.decision;
        if (decision === 'submitted') {
          if (isPlausible) stats.submittedPlausible += 1;
          else stats.submittedImplausible += 1;
        } else {
          // 'skipped' or undecided (dialog cancelled)
          if (isPlausible) stats.skipped += 1;
          else stats.implausibleSkipped += 1;
        }
      } else {
        const bucket = CATEGORY_TO_BUCKET[row.category];
        if (bucket) stats[bucket] += 1;
      }
    }
  }

  stats.submitted = stats.submittedPlausible + stats.submittedImplausible;
  return stats;
}

export function hasReadingValueValidationError(row: ImportRow): boolean {
  const validation = row.task?.readingValueValidation;
  if (!validation || row.ablesewert === null) return false;
  if (validation.minimumValue == null && validation.maximumValue == null) return false;
  const minimumValue = validation.minimumValue ?? 0;
  const maximumValue = validation.maximumValue ?? Number.MAX_SAFE_INTEGER;
  return !isInRange(row.ablesewert, minimumValue, maximumValue, row.task?.postDecimals ?? 0);
}

export function getRowsWithReadingValueValidationError(group: ImportGroup): ImportRow[] {
  // Already-submitted rows are never resent (see enqueueGroup) — they must not
  // demand a photo/comment just to unblock the Import button for the rest.
  return group.rows.filter((r) => r.category !== 'already_done' && hasReadingValueValidationError(r));
}

// Reading-date window is anchored on the job's execution window (± flags),
// mirroring the Excel export validation. The coarser expired / not_yet
// jobDeadlineCategory below is a separate, job-level classification and is
// not made redundant by this per-row check.
function isReadingDateAllowed(
  raw: RawExcelRow,
  job: JobType | null,
  task: MeterRateTaskType | null,
  applicationConfig?: ApplicationConfig,
): boolean {
  if (!raw.ablesedatum || !task) return true;
  const merged = mergeJobConfig(job?.config, applicationConfig);
  const readingDate = dayjsTZ(raw.ablesedatum).startOf('day');
  const { minAllowedDate, maxAllowedDate } = getAllowedReadingDateRange(job?.jobConstraints, merged);
  return !readingDate.isBefore(minAllowedDate) && !readingDate.isAfter(maxAllowedDate);
}

export function buildImportRows(
  rawRows: RawExcelRow[],
  jobsMap: Map<string, JobType>,
  applicationConfig?: ApplicationConfig,
  today?: string,
  queuedActions?: AnyAction[]
): ImportRow[] {
  const importDate = (today ? dayjsTZ(today) : dayjsTZ()).format('YYYY-MM-DD');
  const normalizedRows = rawRows.map((raw) => fillMissingReadingDate({ ...raw, ...resolveRawRowMatch(raw, jobsMap) }, importDate));
  const seenExact = new Map<string, number>();
  const seenConflict = new Map<string, number>();
  const conflictKeys = new Set<string>();
  const forcedCategories = new Array<ImportCategory | null>(normalizedRows.length).fill(null);

  // C2: detect duplicate/conflict rows using conflictKeys set for correct 3+-row handling
  normalizedRows.forEach((raw, idx) => {
    if (!raw.jobId || !raw.taskId) return;
    const exactKey = `${raw.jobId}|${raw.taskId}|${raw.ablesedatum}|${raw.ablesewert}`;
    const conflictKey = `${raw.jobId}|${raw.taskId}`;

    if (conflictKeys.has(conflictKey)) {
      forcedCategories[idx] = 'duplicate_conflict';
    } else if (seenExact.has(exactKey)) {
      forcedCategories[idx] = 'duplicate_exact';
    } else if (seenConflict.has(conflictKey)) {
      conflictKeys.add(conflictKey);
      forcedCategories[idx] = 'duplicate_conflict';
      forcedCategories[seenConflict.get(conflictKey)!] = 'duplicate_conflict';
    } else {
      seenExact.set(exactKey, idx);
      seenConflict.set(conflictKey, idx);
    }
  });

  // C1: pre-compute deadline category per job (expired/not_yet)
  const jobDeadlineCategory = new Map<string, ImportCategory | null>();
  // Standardhinweise sind pro Auftrag konfigurierbar (mergeJobConfig ersetzt die
  // Liste, statt sie zu mischen) — deshalb je Auftrag merken.
  const jobDefaultComments = new Map<string, DefaultJobComment[] | undefined>();
  jobsMap.forEach((job, jobId) => {
    const merged = mergeJobConfig(job.config, applicationConfig);
    jobDefaultComments.set(jobId, merged?.meterReading?.defaultComments);
    const durationLatest = merged?.features?.durationSendingReadingValuesBasedOnLatestExecutionIsAllowed ?? null;
    const durationEarliest = merged?.features?.durationSendingReadingValuesBasedOnEarliestExecutionIsAllowed ?? null;

    const latest = job.jobConstraints?.latestExecution;
    if (latest && durationLatest !== null) {
      const effectiveLatest = dayjs(latest).add(durationLatest, 'days');
      if (dayjs().isAfter(effectiveLatest)) {
        jobDeadlineCategory.set(jobId, 'expired');
        return;
      }
    }

    const earliest = job.jobConstraints?.earliestExecution;
    if (earliest && durationEarliest !== null) {
      const effectiveEarliest = dayjs(earliest).subtract(durationEarliest, 'days');
      if (dayjs().isBefore(effectiveEarliest)) {
        jobDeadlineCategory.set(jobId, 'not_yet');
        return;
      }
    }

    jobDeadlineCategory.set(jobId, null);
  });

  return normalizedRows.map((raw, idx) => {
    const job = raw.jobId ? (jobsMap.get(raw.jobId) ?? null) : null;
    const task = job?.meter?.meterRateTasks?.find((t) => t.taskId === raw.taskId) ?? null;

    const deadlineCategory = raw.jobId ? jobDeadlineCategory.get(raw.jobId) ?? null : null;
    const duplicateCategory = forcedCategories[idx];
    const category =
      deadlineCategory
      ?? duplicateCategory
      ?? (!isReadingDateAllowed(raw, job, task, applicationConfig) ? 'invalid_reading_date' : categorizeRow(raw, jobsMap, applicationConfig, queuedActions));

    // Für eine nicht zuordenbare jobId (not_found) gibt es keine
    // auftragsspezifische Liste — dann gilt die mandantenweite, wie bei Zeilen
    // ganz ohne jobId.
    const defaultComments = raw.jobId && jobDefaultComments.has(raw.jobId)
      ? jobDefaultComments.get(raw.jobId)
      : applicationConfig?.meterReading?.defaultComments;

    return {
      ...raw,
      ablesehinweis: canonicalizeReadingHint(raw.ablesehinweis, defaultComments),
      job,
      task,
      category,
    };
  });
}

// ─── ExcelJS file parsing ─────────────────────────────────────────────────────

// Lowercased, trimmed cell text — the normalized form used for header/key lookup.
const normCell = (v: ExcelJS.CellValue): string => String(v ?? '').toLowerCase().trim();

const rowLookup = (ws: ExcelJS.Worksheet, rowNumber: number): Record<string, number> => {
  const map: Record<string, number> = {};
  ws.getRow(rowNumber).eachCell((cell, colNumber) => {
    const k = normCell(cell.value);
    if (k) map[k] = colNumber;
  });
  return map;
};

const cellStr = (row: ExcelJS.Row, col: number | undefined): string | null => {
  if (!col) return null;
  const val = row.getCell(col).value;
  if (val === null || val === undefined) return null;
  return String(val).trim() || null;
};

// Header aliases for the fallback path (files without a technical key row).
// Technical ID columns are intentionally strict: only the machine headers from
// our export are accepted. User-facing reading columns keep localized aliases so
// older/external files can still be matched by their visible headers.
const legacyReadingAliases = () => {
  const lngs = ['de-FORMAL', 'de-INFORMAL', 'en-FORMAL'];
  const collect = (labelKey: string, extras: string[]): string[] => {
    const set = new Set(extras.map((s) => s.toLowerCase()));
    lngs.forEach((lng) => {
      const v = String(i18n.t(labelKey, { lng })).toLowerCase().trim();
      if (v && v !== labelKey.toLowerCase()) set.add(v);
    });
    return Array.from(set);
  };
  return {
    jobId: ['__jobid'],
    taskId: ['__taskid'],
    zaehler: collect('management.excelExport.columns.fields.meterNumber', ['zählernummer', 'zahlernummer']),
    obis: collect('management.excelExport.columns.fields.obis', ['obis']),
    date: collect('management.excelExport.columns.fields.readingDate', ['ablesedatum']),
    value: collect('management.excelExport.columns.fields.readingValue', ['ablesewert', 'zählerstand']),
    // 'reading note' is the pre-canonicalization EN wording (now 'Reading Hint') —
    // kept so files exported before that rename still match on re-import.
    hint: collect('management.excelExport.columns.fields.readingHint', ['ablesehinweis', 'reading note']),
  };
};

// Locates the visible header row within the first rows of the sheet: the row
// with the most alias hits wins. Row 1 in legacy exports; row 2 in the
// group-band layout, whose band labels ("Zähler"/"Ablesung"/…) match no alias.
function findHeaderRowNumber(ws: ExcelJS.Worksheet, aliases: ReturnType<typeof legacyReadingAliases>): number {
  const aliasSets = [aliases.jobId, aliases.taskId, aliases.zaehler, aliases.obis, aliases.date, aliases.value, aliases.hint];
  const maxScan = Math.min(5, ws.rowCount || 5);
  let headerRowNumber = 1;
  let bestHits = -1;
  for (let r = 1; r <= maxScan; r++) {
    const lookup = rowLookup(ws, r);
    const hits = aliasSets.filter((names) => names.some((n) => lookup[n] !== undefined)).length;
    if (hits > bestHits) {
      bestHits = hits;
      headerRowNumber = r;
    }
  }
  return headerRowNumber;
}

export async function parseExcelBuffer(arrayBuffer: ArrayBuffer, fileIndex: number = 0): Promise<RawExcelRow[]> {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);
  const ws = workbook.worksheets[0];

  // The visible header row position varies by layout: row 1 in legacy exports,
  // row 2 in the group-band layout (row 1 carries the merged band labels
  // "Zähler"/"Ablesung"/... there, which match no alias). findHeaderRowNumber
  // picks the scanned row with the most alias hits. Hidden system columns
  // (job/task id) carry a literal machine-readable header ("__jobId"), so
  // their identity survives renames and language changes. Visible columns
  // (reading value/date/hint, meter number, obis) carry the localized label,
  // so they're matched via i18n alias lookup — which also covers externally
  // authored files that never had our machine headers to begin with.
  const aliases = legacyReadingAliases();
  const headerRowNumber = findHeaderRowNumber(ws, aliases);
  const headers = rowLookup(ws, headerRowNumber);
  const byAliases = (names: string[]): number | undefined => {
    for (const n of names) {
      if (headers[n] !== undefined) return headers[n];
    }
    return undefined;
  };
  const cols = {
    jobId: byAliases(aliases.jobId),
    taskId: byAliases(aliases.taskId),
    zaehler: byAliases(aliases.zaehler),
    obis: byAliases(aliases.obis),
    date: byAliases(aliases.date),
    value: byAliases(aliases.value),
    hint: byAliases(aliases.hint),
  };
  const firstDataRow = headerRowNumber + 1;

  const rows: RawExcelRow[] = [];
  ws.eachRow((row, rowNumber) => {
    if (rowNumber < firstDataRow) return;

    const dateCellValue = cols.date ? row.getCell(cols.date).value : null;
    const rawDate = dateCellValue instanceof Date ? dateCellValue : cellStr(row, cols.date);

    rows.push({
      jobId: cellStr(row, cols.jobId),
      taskId: cellStr(row, cols.taskId),
      zaehlernummer: cellStr(row, cols.zaehler),
      obis: cellStr(row, cols.obis),
      ablesedatum: normalizeDateToISO(rawDate as any),
      ablesewert: normalizeDecimal(cellStr(row, cols.value)),
      ablesehinweis: cellStr(row, cols.hint),
      _rowIndex: rowNumber,
      _fileIndex: fileIndex,
    });
  });

  return rows.filter(
    (r) => r.jobId || r.taskId || r.zaehlernummer || r.obis || r.ablesedatum || r.ablesewert !== null
  );
}

// ─── Download remaining original files ────────────────────────────────────────

// exceljs's own .d.ts doesn't declare `dataValidations` (the address-indexed
// side table backing `cell.dataValidation`) on Worksheet, even though it
// exists at runtime — see exceljs/lib/doc/worksheet.js.
type WorksheetWithDataValidations = ExcelJS.Worksheet & {
  dataValidations: { model: Record<string, ExcelJS.DataValidation | undefined> };
};

export async function reExportOriginalFiles(
  files: Array<{ buffer: ArrayBuffer; name: string }>,
  importedRowsByFile: Map<number, Set<number>>
): Promise<void> {
  const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const blobs: Array<{ name: string; blob: Blob }> = [];
  const reExportDate = dayjs().format('YYYY-MM-DD');
  const suffix = i18n.t('management.import.summary.reExportFileSuffix') as string;

  for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
    const importedRows = importedRowsByFile.get(fileIndex);
    if (!importedRows || importedRows.size === 0) continue;

    const { buffer, name } = files[fileIndex];
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
    const ws = workbook.worksheets[0];

    // Header rows: 1 in legacy exports, 2 in the group-band layout (band +
    // header) — detected like the parser via alias hits; data starts one row
    // below the header, so the header row number equals the header row count.
    const headerRowCount = findHeaderRowNumber(ws, legacyReadingAliases());

    // Un-share shared formulas (ExcelJS/Excel may collapse structurally identical
    // per-row formulas into a "shared formula" group with one master cell). If the
    // master row gets spliced out below, the remaining clone cells are left without
    // a master and ExcelJS throws "Shared Formula master must exist..." on write.
    // Resolving each clone to its own explicit (but still live/calculating) formula
    // via the translated `cell.formula` getter removes that dependency on the master
    // row while keeping the formula itself intact.
    ws.eachRow((row) => {
      row.eachCell((cell) => {
        if (cell.model?.sharedFormula) {
          cell.value = { formula: cell.formula, result: cell.result };
        }
      });
    });

    // Build an old-row → new-row map BEFORE deleting anything, so every cell
    // reference inside a formula/dataValidation formula — same-row or
    // cross-row — can be repointed to where its row actually ended up,
    // instead of assuming every reference points to the formula's own row.
    const rowMap = new Map<number, number>();
    {
      let shift = 0;
      for (let r = 1; r <= ws.rowCount; r++) {
        if (importedRows.has(r)) {
          shift++;
          continue;
        }
        rowMap.set(r, r - shift);
      }
    }

    // Delete imported rows bottom-to-top to avoid index-shift issues
    const sortedDesc = Array.from(importedRows).sort((a, b) => b - a);
    for (const rowIndex of sortedDesc) {
      ws.spliceRows(rowIndex, 1);
    }

    // Post-splice check: skip if no data rows remain (all rows were imported)
    if (ws.rowCount <= headerRowCount) continue;

    // Repair dataValidation/cell formula strings — row numbers shifted after
    // spliceRows. Each reference within a formula is remapped individually via
    // rowMap (falling back to the original row number if it was itself
    // deleted), so this is correct for cross-row formulas too, not just
    // same-row ones. Used below both to rebuild dataValidations and to repair
    // plain cell formulas.
    const remapFormula = (formula: string): string =>
      formula.replace(/(\$?[A-Z]+)(\d+)/g, (_match: string, col: string, rowStr: string) => {
        const oldRow = Number(rowStr);
        return `${col}${rowMap.get(oldRow) ?? oldRow}`;
      });

    // Rebuild dataValidations from scratch instead of mutating in place.
    // `cell.dataValidation` is not a per-cell attribute in exceljs — it's a
    // lookup into an address-indexed side table (ws.dataValidations.model),
    // and spliceRows() never touches that table. Left alone, a surviving row
    // would read whatever validation happens to sit at ITS OWN (post-splice)
    // address — i.e. a deleted row's rule, not its own — and any rule whose
    // address nothing moved into afterwards would be left dangling below the
    // new data end. `remapCache` remaps each validation object exactly once,
    // even though the very same object can be referenced by several
    // addresses at once (e.g. one rule Excel applied across a whole selected
    // range shares one object across its `sqref`).
    const remapCache = new Map<ExcelJS.DataValidation, ExcelJS.DataValidation>();
    const remapValidation = (validation: ExcelJS.DataValidation): ExcelJS.DataValidation => {
      const cached = remapCache.get(validation);
      if (cached) return cached;
      const remapped: ExcelJS.DataValidation = {
        ...validation,
        formulae: validation.formulae?.map(remapFormula),
      };
      remapCache.set(validation, remapped);
      return remapped;
    };

    // exceljs's own .d.ts doesn't declare `dataValidations` on Worksheet even
    // though it exists at runtime (see lib/doc/worksheet.js) — a narrow local
    // type stands in for the missing declaration instead of reaching for `any`.
    const wsValidations = (ws as WorksheetWithDataValidations).dataValidations;
    const oldValidations = wsValidations.model;
    const newValidations: Record<string, ExcelJS.DataValidation> = {};
    Object.keys(oldValidations).forEach((address) => {
      const validation = oldValidations[address];
      if (!validation) return;
      const match = address.match(/^([A-Z]+)(\d+)$/);
      if (!match) return; // defensive: unexpected address shape, drop rather than misplace
      const [, col, rowStr] = match;
      const newRow = rowMap.get(Number(rowStr));
      if (newRow === undefined) return; // row was deleted — drop the orphaned rule
      newValidations[`${col}${newRow}`] = remapValidation(validation);
    });
    wsValidations.model = newValidations;

    ws.eachRow((row) => {
      row.eachCell((cell) => {
        if (cell.type === ExcelJS.ValueType.Formula) {
          cell.value = { formula: remapFormula(cell.formula), result: cell.result };
        }
      });
    });

    const outputBuffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([outputBuffer as ArrayBuffer], { type: XLSX_MIME });
    const baseName = name.replace(/\.xlsx$/i, '');
    blobs.push({ name: `${baseName}_${reExportDate}_${suffix}.xlsx`, blob });
  }

  if (blobs.length === 0) return;

  const zipName = `${reExportDate} - ${suffix}.zip`;
  await download(blobs, zipName);
}
